import { Parser } from '~/api';
import { SwitchGroup } from '~/api/models';
import RequestService from './request';

export interface SwitchBoardEgg {
    id: string;
    name: string;
    tags: string[];
    thumbnail: string | null;
    switch_disabled: boolean;
    switch_group_ids: string[];
}

export interface SwitchBoardGroup {
    id: string;
    name: string;
    eggs: SwitchBoardEgg[];
}

export interface SwitchBoard {
    groups: SwitchBoardGroup[];
    // Every egg, regardless of grouping -- the board's drag source. An egg can
    // belong to many groups, so it stays here and is cloned into each group.
    eggs: SwitchBoardEgg[];
}

interface CreateSwitchGroupRequest {
    name: string;
}

interface UpdateSwitchGroupRequest {
    id: string;
    name: string;
}

interface DeleteSwitchGroupRequest {
    id: string;
}

class SwitchGroupsService {
    getAll(req: PaginatableRequest): Promise<ListResponse> {
        return RequestService.get('/switch-groups', { ...req }).then(Parser.parse);
    }

    getBoard(): Promise<SwitchBoard> {
        return RequestService.get('/switch-groups/board');
    }

    // Wipes every group and rebuilds one per tag, assigning each egg to its tags.
    groupByTag(): Promise<void> {
        return RequestService.post('/switch-groups/group-by-tag');
    }

    // Bulk enable/disable per-egg switching. switch_disabled is independent of
    // grouping, so any egg can be toggled here -- grouped or not.
    bulkSwitchConfig(data: { enabled_ids: string[]; disabled_ids: string[] }): Promise<void> {
        return RequestService.post('/switch-groups/switch-config', data);
    }

    create(data: CreateSwitchGroupRequest): Promise<SwitchGroup> {
        return RequestService.post('/switch-groups', data).then(Parser.parse);
    }

    reset(): Promise<SwitchGroup> {
        return RequestService.post('/switch-groups/reset');
    }

    update(data: UpdateSwitchGroupRequest): Promise<SwitchGroup> {
        return RequestService.put(`/switch-groups/${data.id}`, { name: data.name }).then(Parser.parse);
    }

    delete(data: DeleteSwitchGroupRequest): Promise<void> {
        return RequestService.delete(`/switch-groups/${data.id}`);
    }

    // Attaching/detaching an egg uses the dedicated switch-group endpoints (gated
    // on switch_group.update) rather than the per-egg switch-config endpoint, so
    // the board stands on its own permission family. Attach is idempotent.
    attachEgg(switchGroupId: string, eggId: string): Promise<void> {
        return RequestService.post(`/switch-groups/${switchGroupId}/eggs/${eggId}`);
    }

    detachEgg(switchGroupId: string, eggId: string): Promise<void> {
        return RequestService.delete(`/switch-groups/${switchGroupId}/eggs/${eggId}`);
    }
}

export default new SwitchGroupsService();
